import { useEffect, useRef } from 'react'

import { createFreePreviewMember } from 'actions/LuxPlusActions'
import config from 'constants/config'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useLuxPlusFreePreviewUtils from 'luxPlus/hooks/useLuxPlusFreePreviewUtils'

const COOLDOWN_MS = LUXURY_PLUS.FREE_PREVIEW_AUTO_ENROL_COOLDOWN_DAYS * 24 * 60 * 60 * 1000

function isWithinFreePreviewCooldown(lastFreePreview: App.LuxPlusLastFreePreview): boolean {
  return Date.now() < new Date(lastFreePreview.endDate).getTime() + COOLDOWN_MS
}

/**
 * Silently enrols an existing customer into the LuxPlus+ Free Preview when their region + traffic
 * channel matches a `free_preview_auto_enrol_for_existing_users` rule (resolved from svc-promo
 * into `marketing.freePreviewAutoEnrolForExistingUsers`).
 *
 * The new-user equivalent lives in userMiddleware and fires on the signup event. This one keys off
 * *being* logged in rather than off a login event: the campaigns that target existing customers
 * are mostly EDM traffic, and most of those visitors arrive with a live session, so they never
 * produce a login event to hang the enrolment off. Keying off the state covers both — a visitor
 * who was already signed in, and one who signs in after landing.
 *
 * Customers who registered in this session are left to the new-user path, so the two audiences
 * stay disjoint and each enrolment carries the trigger the celebration modal reads to decide
 * whether to call the preview a welcome gift.
 */
function useLuxPlusFreePreviewAutoEnrolExistingUsers() {
  const dispatch = useAppDispatch()
  const loggedIn = useAppSelector(selectLoggedIn)
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const settingEnabled = useAppSelector(state => state.marketing.freePreviewAutoEnrolForExistingUsers?.enabled ?? false)
  const isNewUser = useAppSelector(state => state.loyalty.enrolment === 'newUserRegistration')
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const subscription = useAppSelector(state => state.auth.account.luxPlus.member.subscription)
  const { canJoinFreePreview } = useLuxPlusFreePreviewUtils()
  const enrolRequestedRef = useRef(false)

  const isWithinCooldownInAnyRegion = subscription.lastFreePreviews?.some(isWithinFreePreviewCooldown) ?? false
  // canJoinFreePreview is region-scoped, so a live preview from another region doesn't register
  // there. The cooldown covers this too, but only once svc-membership returns the history.
  const hasLiveFreePreview = subscription.item?.tier === 'free_preview'

  const shouldEnrol =
    config.ENABLE_AUTO_ENROLLMENT_FOR_FREE_PREVIEW_EXISTING_USERS &&
    settingEnabled &&
    loggedIn &&
    luxPlusEnabled &&
    !isNewUser &&
    !isSpoofed &&
    canJoinFreePreview &&
    !hasLiveFreePreview &&
    !isWithinCooldownInAnyRegion &&
    // createFreePreviewMember bails while the membership is still loading, so waiting for it to
    // settle keeps us from spending the single attempt on a guaranteed no-op.
    !subscription.fetching &&
    !subscription.error

  useEffect(() => {
    if (!shouldEnrol || enrolRequestedRef.current) return

    // One attempt per session. createFreePreviewMember() no-ops for members who can already view
    // benefits; anything else that slips past the checks above is svc-membership's call to
    // accept or reject. Failures stay silent because the customer never asked for this.
    enrolRequestedRef.current = true
    dispatch(createFreePreviewMember({
      trigger: 'auto-free-preview-enroll-for-existing-user',
      silentError: true,
    }))
  }, [dispatch, shouldEnrol])
}

export default useLuxPlusFreePreviewAutoEnrolExistingUsers
